// src/store/slices/secondaryLineSlice.ts
/**
 * Secondary (double) number line configuration with localStorage persistence
 * Implements Section 5.3 of the specification
 * Phase 3: Enhanced with NumberFormatConfig
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SecondaryLineState, Fraction, NumberFormatConfig, defaultNumberFormatConfig } from '../../types/redux';

const SECONDARY_LINE_STORAGE_KEY = 'nl:secondaryLine';

// Default secondary line state with Phase 3 enhancements
const defaultSecondaryLineState: SecondaryLineState = {
  enabled: false, // Disabled by default
  scaleFactor: { n: 2, d: 1 }, // 2x by default
  inheritFormatFromPrimary: true, // Default to inheriting from primary line
  numberFormat: { ...defaultNumberFormatConfig, type: 'fraction' },
  locked: true, // proportional by default
  offset: 0, // T-24: Added offset
  visible: true, // T-24: Added visibility
  hideAllLabels: false, // Show all labels by default
};

/**
 * Migrate legacy state to Phase 3 format
 */
function migrateLegacyState(stored: any): SecondaryLineState {
  const state = { ...defaultSecondaryLineState, ...stored };
  
  // Migrate old numberType to new numberFormat
  if (stored.numberType && !stored.numberFormat) {
    const typeMap: Record<string, NumberFormatConfig['type']> = {
      'integer': 'integer',
      'decimal': 'decimal',
      'fraction': 'fraction',
      'percent': 'percentage',
    };
    
    state.numberFormat = {
      ...defaultNumberFormatConfig,
      type: typeMap[stored.numberType] || 'integer',
      decimalPlaces: 2,
      fractionFormat: stored.fractionDisplay?.format || 'improper',
      fractionSimplify: stored.fractionDisplay?.simplify !== false,
    };
  } else if (!stored.numberFormat) {
    // Ensure numberFormat exists even if no migration needed
    state.numberFormat = { ...defaultNumberFormatConfig };
  }

  // Default inheritance: preserve explicit user formats if present, otherwise inherit
  if (typeof stored?.inheritFormatFromPrimary === 'boolean') {
    state.inheritFormatFromPrimary = stored.inheritFormatFromPrimary;
  } else if (stored && stored.numberFormat) {
    state.inheritFormatFromPrimary = false;
  }
  
  return state;
}

/**
 * Load secondary line state from localStorage with migration
 */
function loadSecondaryLineFromStorage(): SecondaryLineState {
  try {
    const stored = localStorage.getItem(SECONDARY_LINE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new property additions
      let secondaryLine = migrateLegacyState(parsed);
      let updated = false;

      // Migrate legacy default scale factor of 1/2 to new 2/1
      if (secondaryLine.scaleFactor?.n === 1 && secondaryLine.scaleFactor?.d === 2) {
        secondaryLine = {
          ...secondaryLine,
          scaleFactor: defaultSecondaryLineState.scaleFactor,
        };
        updated = true;
      }

      // Check if migration happened (old numberType exists but new numberFormat was created)
      if (parsed.numberType && !parsed.numberFormat) {
        updated = true;
      }

      if (updated) {
        saveSecondaryLineToStorage(secondaryLine);
      }
      console.log('📐 Loaded and migrated secondary line state:', secondaryLine);
      return secondaryLine;
    }
  } catch (error) {
    console.warn('Failed to load secondary line from storage:', error);
  }
  
  // If no stored state, try to get default scale factor from preferences
  try {
    const preferencesStored = localStorage.getItem('nl:preferences');
    if (preferencesStored) {
      const preferences = JSON.parse(preferencesStored);
      if (preferences.defaultScaleFactor) {
        const stateWithPrefs = { 
          ...defaultSecondaryLineState, 
          scaleFactor: preferences.defaultScaleFactor 
        };
        console.log('📐 Using preferences default scale factor:', stateWithPrefs);
        return stateWithPrefs;
      }
    }
  } catch (error) {
    console.warn('Failed to load preferences for default scale factor:', error);
  }
  
  console.log('📐 Using default secondary line state');
  return defaultSecondaryLineState;
}

/**
 * Save secondary line state to localStorage
 */
function saveSecondaryLineToStorage(secondaryLine: SecondaryLineState): void {
  try {
    localStorage.setItem(SECONDARY_LINE_STORAGE_KEY, JSON.stringify(secondaryLine));
  } catch (error) {
    console.warn('Failed to save secondary line to storage:', error);
  }
}

const initialState: SecondaryLineState = loadSecondaryLineFromStorage();

export const secondaryLineSlice = createSlice({
  name: 'secondaryLine',
  initialState,
  reducers: {
    /**
     * Enable or disable the secondary line
     */
    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set whether the secondary line inherits the primary format
     */
    setInheritFormatFromPrimary: (state, action: PayloadAction<boolean>) => {
      state.inheritFormatFromPrimary = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle secondary line on/off
     */
    toggleSecondaryLine: (state) => {
      console.log('🔄 toggleSecondaryLine reducer called - current state:', state.enabled);
      state.enabled = !state.enabled;
      console.log('🔄 toggleSecondaryLine reducer completed - new state:', state.enabled);
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set the scale factor (secondary = primary * scaleFactor)
     */
    setScaleFactor: (state, action: PayloadAction<Fraction>) => {
      state.scaleFactor = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set number type for secondary line (DEPRECATED - use setFormatType)
     */
    setSecondaryNumberType: (state, action: PayloadAction<'integer' | 'decimal' | 'fraction' | 'percent'>) => {
      // Migrate to new format
      const typeMap: Record<string, NumberFormatConfig['type']> = {
        'integer': 'integer',
        'decimal': 'decimal',
        'fraction': 'fraction',
        'percent': 'percentage',
      };
      state.numberFormat.type = typeMap[action.payload] || 'integer';
      state.numberType = action.payload; // Keep for backward compatibility
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle fraction format for secondary line (DEPRECATED - use updateNumberFormatProperty)
     */
    toggleSecondaryFractionFormat: (state) => {
      if (state.fractionDisplay) {
        state.fractionDisplay.format = 
          state.fractionDisplay.format === 'mixed' ? 'improper' : 'mixed';
      }
      state.numberFormat.fractionFormat = 
        (state.numberFormat.fractionFormat === 'mixed' ? 'improper' : 'mixed');
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle fraction simplification for secondary line (DEPRECATED - use updateNumberFormatProperty)
     */
    toggleSecondaryFractionSimplify: (state) => {
      if (state.fractionDisplay) {
        state.fractionDisplay.simplify = !state.fractionDisplay.simplify;
      }
      state.numberFormat.fractionSimplify = !state.numberFormat.fractionSimplify;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set complete number format configuration
     */
    setNumberFormat: (state, action: PayloadAction<NumberFormatConfig>) => {
      state.numberFormat = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Update specific number format property
     */
    updateNumberFormatProperty: <K extends keyof NumberFormatConfig>(
      state: SecondaryLineState,
      action: PayloadAction<{ key: K; value: NumberFormatConfig[K] }>
    ) => {
      const { key, value } = action.payload;
      state.numberFormat[key] = value as any;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set format type (convenience action)
     */
    setFormatType: (state, action: PayloadAction<NumberFormatConfig['type']>) => {
      // Ensure numberFormat exists
      if (!state.numberFormat) {
        state.numberFormat = { ...defaultNumberFormatConfig };
      }
      state.numberFormat.type = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set decimal places (0-3 or 'auto')
     */
    setDecimalPlaces: (state, action: PayloadAction<0 | 1 | 2 | 3 | 'auto'>) => {
      state.numberFormat.decimalPlaces = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set currency symbol
     */
    setCurrencySymbol: (state, action: PayloadAction<string>) => {
      state.numberFormat.currencySymbol = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set custom unit
     */
    setCustomUnit: (state, action: PayloadAction<string>) => {
      state.numberFormat.customUnit = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set whether secondary line is locked to primary proportions
     */
    setLocked: (state, action: PayloadAction<boolean>) => {
      state.locked = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Toggle hide all labels for secondary line
     */
    toggleHideAllLabels: (state) => {
      state.hideAllLabels = !state.hideAllLabels;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Set hide all labels for secondary line
     */
    setHideAllLabels: (state, action: PayloadAction<boolean>) => {
      state.hideAllLabels = action.payload;
      saveSecondaryLineToStorage(state);
    },

    /**
     * Reset secondary line to defaults
     */
    resetSecondaryLine: (state) => {
      Object.assign(state, defaultSecondaryLineState);
      saveSecondaryLineToStorage(state);
    },

    /**
     * Load secondary line state from shared scene
     */
    loadSecondaryLineState: (state, action: PayloadAction<SecondaryLineState>) => {
      Object.assign(state, action.payload);
      saveSecondaryLineToStorage(state);
    },
  },
});

export const {
  setEnabled,
  setInheritFormatFromPrimary,
  toggleSecondaryLine,
  setScaleFactor,
  setSecondaryNumberType,
  toggleSecondaryFractionFormat,
  toggleSecondaryFractionSimplify,
  setNumberFormat,
  updateNumberFormatProperty,
  setFormatType,
  setDecimalPlaces,
  setCurrencySymbol,
  setCustomUnit,
  setLocked,
  toggleHideAllLabels,
  setHideAllLabels,
  resetSecondaryLine,
  loadSecondaryLineState,
} = secondaryLineSlice.actions;

export default secondaryLineSlice.reducer;